import React, { useState } from "react";
import { LOGIN_ROUTE } from "../appMode";
import { useNavigate } from "react-router-dom";
import { FetchFromServer } from "../api/ServerApi";
import {
  sanitizeMobileInput,
  mobileValidationError,
  isValidMobile,
  openOtpWhatsApp,
  OTP_SENDER_WHATSAPP,
} from "../utils/mobileNumber";



const SmartifySignup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    Surname: "",
    Name: "",
    FatherHusband: "",
    Password: "",
    EmailId: "",
    MobileNumber: "",
  });

  // Only used in reset mode (existing number) — the new password must be typed twice.
  const [confirmPassword, setConfirmPassword] = useState("");

  const [busy, setBusy] = useState(false);

  // Whether this number already has an account.
  //   null = not checked yet | "checking" | "existing" | "new" | "error"
  const [accountStatus, setAccountStatus] = useState(null);

  // Set once the OTP has actually gone out, so the screen can offer the
  // "Open WhatsApp" shortcut instead of jumping straight to the code entry.
  const [otpSentTo, setOtpSentTo] = useState("");

  // An already-registered number turns this screen into a password reset:
  // the person clearly has an account, so asking for their name and email
  // again is pointless. OTP proves ownership, then the password is replaced.
  const isReset = accountStatus === "existing";

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /**
   * Look the number up as soon as it is complete, so the user is told
   * "already registered" while typing rather than after filling the whole form.
   * Returns the status so the submit handler can reuse the result.
   */
  const checkAccount = async (mobile) => {
    if (!isValidMobile(mobile)) {
      setAccountStatus(null);
      return null;
    }

    setAccountStatus("checking");
    try {
      // GetUserDetailsbyMobileNumber exists only under /api/, hence open:true.
      const existing = await FetchFromServer(
        "GetUserDetailsbyMobileNumber",
        { MobileNumber: Number(mobile) },
        { plain: true, open: true }
      );
      const found = Array.isArray(existing) && existing.length > 0;
      const status = found ? "existing" : "new";
      setAccountStatus(status);
      return status;
    } catch (err) {
      console.error("Could not check whether the number is registered", err);
      setAccountStatus("error");
      return "error";
    }
  };

  // Normalises +91 / leading 0 / spaces and caps at 10 digits as the user types.
  const handleMobileChange = (e) => {
    const cleaned = sanitizeMobileInput(e.target.value);
    setFormData((prev) => ({ ...prev, MobileNumber: cleaned }));
    setOtpSentTo("");

    if (isValidMobile(cleaned)) checkAccount(cleaned);
    else setAccountStatus(null);
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    // In reset mode only the mobile + new password matter; the account already
    // has the name/email fields filled in.
    if (isReset) {
      if (!formData.MobileNumber || !formData.Password || !confirmPassword) {
        alert("Please enter your mobile number and the new password twice.");
        return;
      }
      if (formData.Password !== confirmPassword) {
        alert("The two passwords do not match.");
        return;
      }
    } else if (
      !formData.Surname ||
      !formData.Name ||
      !formData.FatherHusband ||
      !formData.Password ||
      !formData.MobileNumber
    ) {
      alert("Please fill all fields.");
      return;
    }

    // Password must be exactly 4 digits
    if (!/^\d{4}$/.test(formData.Password)) {
      alert("Password must be exactly 4 numeric digits.");
      return;
    }

    // Email is optional — only check the format when something was typed.
    if (!isReset) {
      const email = formData.EmailId.trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        alert("Please enter a valid email address, or leave the field empty.");
        return;
      }
    }

    // Mobile number — the backend rejects anything that is not exactly 10 raw
    // digits, so catch it here with a message that says what is actually wrong.
    const mobile = sanitizeMobileInput(formData.MobileNumber);
    const mobileError = mobileValidationError(mobile);
    if (mobileError) {
      alert(mobileError);
      return;
    }

    // 1. Registered or not? Reuse the live check if it already ran.
    let status = accountStatus;
    if (status !== "existing" && status !== "new") {
      status = await checkAccount(mobile);
    }

    if (status === "error" || status == null) {
      alert("Could not reach the server to check this number. Please try again.");
      return;
    }

    const isExisting = status === "existing";

    // 2. Confirm before sending — OTP messages are billed per send.
    const confirmMessage = isExisting
      ? `${mobile} is already registered.

` +
        `Send an OTP on WhatsApp to verify it is you and set the new password?
` +
        `(No new account will be created.)`
      : `Send an OTP on WhatsApp to ${mobile}?`;

    if (!window.confirm(confirmMessage)) return;

    setBusy(true);
    try {
      // 3. Send OTP
      let otpData;
      try {
        otpData = await FetchFromServer(
          "SendOtpOnWhatsAppbyMobileNumber",
          { MobileNumber: mobile, ServiceBy: "SmartSearch" }
        );
      } catch (err) {
        console.error("Failed to send OTP", err);
        alert("Could not reach the server. Please check your connection and try again.");
        return;
      }

      // Success is an ARRAY carrying the OTP. Every failure comes back as an
      // OBJECT with a `status` — and HTTP 200 either way, so the shape is the
      // only signal. The old code collapsed all of them into "Failed to send
      // OTP", which hid the real reason.
      if (!Array.isArray(otpData) || otpData.length === 0 || !otpData[0].Otp) {
        const status = otpData && !Array.isArray(otpData) ? otpData.status : null;
        console.error("OTP request rejected:", otpData);

        if (status === "Invalid Mobile Number") {
          alert(`The server rejected "${mobile}" as a mobile number. It must be exactly 10 digits.`);
        } else if (status === "failed") {
          alert("The WhatsApp OTP service did not accept the request. Please try again in a few minutes.");
        } else {
          alert("Could not send the OTP. Please try again in a few minutes.");
        }
        return;
      }

      // OtpVerification acts on these once the code checks out:
      //   OtpMode "signup" -> create the account from SignupPayload
      //   OtpMode "reset"  -> set NewPassword on the existing user, then sign in
      localStorage.setItem("Otp", String(otpData[0].Otp));
      localStorage.setItem("OtpMobile", mobile);
      localStorage.setItem("OtpMode", isExisting ? "reset" : "signup");
      localStorage.setItem("FromSignup", isExisting ? "false" : "true");

      if (isExisting) {
        localStorage.setItem("NewPassword", formData.Password);
        localStorage.removeItem("SignupPayload");
      } else {
        localStorage.removeItem("NewPassword");
        localStorage.setItem(
          "SignupPayload",
          JSON.stringify({ ...formData, MobileNumber: mobile })
        );
      }

      // Deliberately NOT navigating away: the user needs the WhatsApp shortcut
      // below to go and read the code first.
      setOtpSentTo(mobile);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <div style={styles.title}>
          {isReset ? "Reset Password" : "SmartSearch Sign Up"}
        </div>
        <form onSubmit={handleSignup} style={styles.loginBox}>
          {!isReset && (
            <>
              <input type="text" name="Surname" autoComplete="family-name" placeholder="Surname" value={formData.Surname} onChange={handleChange} style={styles.input} required />
              <input type="text" name="Name" autoComplete="given-name" placeholder="Name" value={formData.Name} onChange={handleChange} style={styles.input} required />
              <input type="text" name="FatherHusband" autoComplete="off" placeholder="Father/Husband Name" value={formData.FatherHusband} onChange={handleChange} style={styles.input} required />
            </>
          )}
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            name="MobileNumber"
            placeholder="10-digit Mobile Number"
            value={formData.MobileNumber}
            onChange={handleMobileChange}
            maxLength={10}
            style={styles.input}
            required
          />

          {accountStatus === "checking" && (
            <div style={styles.hint}>Checking this number…</div>
          )}
          {accountStatus === "existing" && (
            <div style={{ ...styles.hint, color: "#ffd166" }}>
              ⚠️ Already registered — an OTP will sign you in. No new account will be created.
            </div>
          )}
          {accountStatus === "new" && (
            <div style={{ ...styles.hint, color: "#8ce99a" }}>
              ✓ This number is available.
            </div>
          )}
          {accountStatus === "error" && (
            <div style={{ ...styles.hint, color: "#ffa8a8" }}>
              Could not check this number — you can still continue.
            </div>
          )}
          {!isReset && (
            <input
              type="email"
              name="EmailId"
              autoComplete="email"
              placeholder="Email ID (optional)"
              value={formData.EmailId}
              onChange={handleChange}
              style={styles.input}
            />
          )}
          <input
            type="password"
            name="Password"
            autoComplete="new-password"
            inputMode="numeric"
            placeholder={isReset ? "New 4-digit Password" : "4-digit Numeric Password"}
            value={formData.Password}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only numbers and max 4 digits
              if (/^\d{0,4}$/.test(value)) {
                setFormData((prev) => ({ ...prev, Password: value }));
              }
            }}
            maxLength={4}
            style={styles.input}
            required
          />

          {isReset && (
            <input
              type="password"
              name="ConfirmPassword"
              autoComplete="new-password"
              inputMode="numeric"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,4}$/.test(value)) setConfirmPassword(value);
              }}
              maxLength={4}
              style={styles.input}
              required
            />
          )}


          {otpSentTo ? (
            <>
              <div style={styles.sentBox}>
                OTP sent on WhatsApp to <b>{otpSentTo}</b>
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
                  It arrives from <b>+{OTP_SENDER_WHATSAPP}</b>
                </div>
              </div>

              <button
                type="button"
                onClick={openOtpWhatsApp}
                style={{ ...styles.button, ...styles.whatsapp }}
              >
                Open WhatsApp to read the OTP
              </button>

              <button
                type="button"
                onClick={() => navigate("/verify-otp")}
                style={{ ...styles.button, ...styles.create }}
              >
                I have the OTP — Continue
              </button>
            </>
          ) : (
            <button
              type="submit"
              disabled={busy}
              style={{ ...styles.button, ...styles.create, opacity: busy ? 0.6 : 1 }}
            >
              {busy ? "Please wait..." : isReset ? "Send OTP to reset password" : "Send OTP"}
            </button>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() => navigate(LOGIN_ROUTE)}
            style={{ ...styles.button, ...styles.login }}
          >
            Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  body: {
    backgroundImage:
      'url("https://wallpapers.com/images/hd/usa-chicago-riverwalk-at-night-1jaq0bwc5dc0g6n0.jpg")',
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "cover",
    backgroundAttachment: "fixed",
    margin: 0,
    fontFamily: "Helvetica, Arial, sans-serif",
    // minHeight (not height) + auto overflow: the form is taller than a short
    // laptop viewport once the account hint and the OTP panel appear, and a
    // fixed 100vh silently clipped "Back to Login" off the bottom.
    minHeight: "100vh",
    overflowY: "auto",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "12px",
    boxSizing: "border-box",
  },
  // Compact on purpose: this form has six fields plus a status hint, and it
  // must fit a short laptop viewport without scrolling.
  title: {
    fontSize: "26px",
    color: "white",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  loginBox: {
    background: "rgba(255,255,255,0.1)",
    padding: "14px 16px",
    width: "340px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    textAlign: "center",
  },
  input: {
    width: "100%",
    padding: "9px 12px",
    margin: "4px 0",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "15px",
    boxSizing: "border-box",
  },
  button: {
    width: "100%",
    padding: "10px",
    marginTop: "6px",
    fontSize: "15px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  login: {
    backgroundColor: "#1877f2",
    color: "white",
  },
  create: {
    backgroundColor: "#42b72a",
    color: "white",
  },
  whatsapp: {
    backgroundColor: "#25D366",
    color: "white",
  },
  hint: {
    fontSize: 11.5,
    color: "#e9ecef",
    textAlign: "left",
    margin: "2px 0 4px",
    padding: "4px 7px",
    lineHeight: 1.3,
    background: "rgba(0,0,0,0.45)",
    borderRadius: 5,
  },
  sentBox: {
    background: "rgba(37, 211, 102, 0.15)",
    border: "1px solid rgba(37, 211, 102, 0.6)",
    color: "white",
    borderRadius: 6,
    padding: "8px 10px",
    margin: "6px 0 2px",
    fontSize: 13,
  },
};

export default SmartifySignup;
  